import React, { useState, useEffect, useRef } from "react";
import { useModalForm } from "../../hooks/useModalForm";
import { selectClass, FIELD_PLACEHOLDER } from "../fields/fieldStyles";
import TextInput from "../fields/TextInput";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import { useTransientMessage } from "../../hooks/useTransientMessage";
import {
  MdVisibility,
  MdVisibilityOff,
  MdCloudUpload,
  MdDelete,
  MdImage,
  MdPerson,
} from "react-icons/md";
import { BiaFitUser } from "../../types/user";
import { validateFields, validationRules } from "../../utils/validation";
import { formatDateForInput } from "../../utils/dateUtils";
import { UserService } from "../../services/userService";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import Button from "../common/Button";
import ModalShell from "../modals/ModalShell";
import { logger } from "../../utils/logger";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: any) => Promise<void>;
  user?: BiaFitUser | null;
  mode: "create" | "edit";
  loading?: boolean;
  error?: string | null;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  mode,
  loading = false,
  error: responseError,
}) => {
  const { user: loggedInUser, refreshUser } = useAuth();
  const {
    values: formData,
    setValue: handleInputChange,
    errors,
    setErrors,
    isSubmitting,
    handleSubmit,
  } = useModalForm<Record<string, string>>({
    isOpen,
    initialValues:
      mode === "edit" && user
        ? {
            email: user.email,
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            gender: user.gender || "",
            date_of_birth: formatDateForInput(user.date_of_birth),
            height: user.height || "",
          }
        : {
            email: "",
            first_name: "",
            last_name: "",
            password: "",
            gender: "",
            date_of_birth: "",
            height: "",
          },
    validate: (v) => {
      const next = validateFields(v, {
        first_name: {
          required: true,
          rules: [
            validationRules.minLength(3),
            validationRules.lettersAndSpacesOnly(),
          ],
        },
        last_name: {
          required: true,
          rules: [
            validationRules.minLength(3),
            validationRules.lettersAndSpacesOnly(),
          ],
        },
        ...(mode === "create" && {
          email: { required: true, rules: [validationRules.email()] },
          password: {
            required: true,
            rules: [
              validationRules.password(8),
              validationRules.passwordStrong(),
            ],
          },
        }),
      });

      if (heightCm.trim() !== "") {
        const heightValue = parseInt(heightCm, 10);
        if (isNaN(heightValue) || heightValue < 50 || heightValue > 250) {
          next.height = "Height must be between 50 and 250 cm";
        }
      }

      return next;
    },
    onSubmit: async (v) => {
      try {
        const finalHeight =
          heightCm.trim() !== "" ? `${parseInt(heightCm, 10)}cm` : "";
        await onSubmit({ ...v, height: finalHeight });
      } catch (error) {
        logger.error("Error submitting form:", error);
      }
    },
  });
  const [showPassword, setShowPassword] = useState(false);

  const [heightCm, setHeightCm] = useState<string>("");

  // Profile image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageSuccess, setImageSuccess] = useTransientMessage(3000);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentProfileImage, setCurrentProfileImage] = useState<string | null>(
    null
  );

  const parseHeight = (heightStr?: string) => {
    const h = (heightStr || "").trim();
    if (!h) {
      setHeightCm("");
      return;
    }
    // Extract numeric value from formats like "180cm", "180 cm", or just "180"
    const match = h.match(/^(\d{2,3})/);
    if (match) {
      setHeightCm(match[1]);
    } else {
      setHeightCm("");
    }
  };

  // Height and the profile image are not form values, so they reset here.
  useEffect(() => {
    if (mode === "edit" && user) {
      parseHeight(user.height);
      setCurrentProfileImage(user.profile_image?.full_path || null);
    } else {
      parseHeight("");
      setCurrentProfileImage(null);
    }
    setImageError(null);
    setImageSuccess(null);
  }, [mode, user, isOpen]);

  // Image upload handlers
  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateImageFile = (file: File): string | null => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 20 * 1024 * 1024; // 20MB

    if (!allowedTypes.includes(file.type)) {
      return "Please upload a valid image file (JPG, JPEG, or PNG)";
    }

    if (file.size > maxSize) {
      return "Image size must be less than 20MB";
    }

    return null;
  };

  const handleImageFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !user || mode !== "edit") return;

    setImageError(null);
    setImageSuccess(null);
    const validationError = validateImageFile(file);
    if (validationError) {
      setImageError(validationError);
      return;
    }

    setUploadingImage(true);
    try {
      const result = await UserService.uploadProfileImage(file);
      if (result.success && result.data) {
        setCurrentProfileImage(result.data.profile_image?.full_path || null);
        setImageSuccess("Profile image uploaded successfully!");

        // If editing my own profile, refresh auth context to update header
        if (loggedInUser && user.id === loggedInUser.id) {
          await refreshUser();
        }
      } else {
        setImageError(result.error || "Failed to upload image");
      }
    } catch (err: any) {
      setImageError(err.message || "Failed to upload image");
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "create" ? "Create New User" : "Edit User"}
      closeDisabled={isSubmitting}
      message={responseError ? { text: responseError, type: "error" } : null}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Saving..."
            disabled={loading}
          >
            {mode === "create" ? "Create User" : "Update User"}
          </Button>
        </>
      }
    >
      <div className="p-6">
        <div className="space-y-4">
          {/* Profile Image Upload - only for edit mode AND my own profile */}
          {mode === "edit" &&
            loggedInUser &&
            user &&
            loggedInUser.id === user.id && (
              <div className="mb-6">
                <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Profile Image
                </label>
                <div className="flex flex-col items-center">
                  <div className="group relative">
                    {currentProfileImage ? (
                      <>
                        <img
                          src={currentProfileImage}
                          alt="Profile"
                          className="h-32 w-32 rounded-full border-4 border-gray-200 object-cover dark:border-gray-600"
                        />
                        {/* Hover overlay */}
                        <div className="bg-black absolute inset-0 flex items-center justify-center rounded-full bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={handleImageUploadClick}
                            disabled={uploadingImage}
                            className="rounded-full bg-white p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
                            title="Change image"
                          >
                            <MdCloudUpload className="h-6 w-6 text-gray-700" />
                          </button>
                        </div>
                        {uploadingImage && (
                          <div className="bg-black absolute inset-0 flex items-center justify-center rounded-full bg-opacity-50">
                            <LoadingSpinner bare size="lg" tone="white" />
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        onClick={handleImageUploadClick}
                        className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-full border-4 border-gray-200 bg-gradient-to-br from-biafit-primary to-biafit-secondary transition-opacity hover:opacity-90 dark:border-gray-600"
                      >
                        {uploadingImage ? (
                          <LoadingSpinner bare size="lg" tone="white" />
                        ) : (
                          <>
                            <MdPerson className="mb-1 h-12 w-12 text-white" />
                            <span className="text-xs font-medium text-white">
                              Upload
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageFileChange}
                    className="hidden"
                  />

                  {/* Upload hint */}
                  {!uploadingImage && (
                    <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                      Click to upload JPG, JPEG, or PNG (max 20MB)
                    </p>
                  )}

                  {/* Success message */}
                  {imageSuccess && (
                    <div className="mt-2 text-sm text-green-600 dark:text-green-400">
                      {imageSuccess}
                    </div>
                  )}

                  {/* Error message */}
                  {imageError && (
                    <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {imageError}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Email - only for create mode */}
          {mode === "create" && (
            <div>
              <Label>Email *</Label>
              <TextInput
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                invalid={!!errors.email}
                placeholder="user@example.com"
                autoComplete="off"
              />
              <FieldError>{errors.email}</FieldError>
            </div>
          )}

          {/* Password - only for create mode */}
          {mode === "create" && (
            <div>
              <Label>Password *</Label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  className={`w-full rounded-md border px-3 py-2 pr-10 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? (
                    <MdVisibilityOff className="h-5 w-5" />
                  ) : (
                    <MdVisibility className="h-5 w-5" />
                  )}
                </button>
              </div>
              <FieldError>{errors.password}</FieldError>
            </div>
          )}

          {/* First Name */}
          <div>
            <Label>First Name *</Label>
            <TextInput
              type="text"
              value={formData.first_name || ""}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
              invalid={!!errors.first_name}
              placeholder="John"
            />
            <FieldError>{errors.first_name}</FieldError>
          </div>

          {/* Last Name */}
          <div>
            <Label>Last Name *</Label>
            <TextInput
              type="text"
              value={formData.last_name || ""}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
              invalid={!!errors.last_name}
              placeholder="Doe"
            />
            <FieldError>{errors.last_name}</FieldError>
          </div>

          {/* Gender */}
          <div>
            <Label>Gender</Label>
            <select
              value={formData.gender || ""}
              onChange={(e) => handleInputChange("gender", e.target.value)}
              className={selectClass(
                false,
                formData.gender ? "" : FIELD_PLACEHOLDER
              )}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <FieldError>{errors.gender}</FieldError>
          </div>

          {/* Date of Birth */}
          <div>
            <Label>Date of Birth</Label>
            <TextInput
              type="date"
              value={formData.date_of_birth || ""}
              onChange={(e) =>
                handleInputChange("date_of_birth", e.target.value)
              }
            />
            <FieldError>{errors.date_of_birth}</FieldError>
          </div>

          {/* Height */}
          <div>
            <Label>Height (cm)</Label>
            <TextInput
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={heightCm}
              onChange={(e) => {
                const digitsOnly = e.target.value
                  .replace(/[^0-9]/g, "")
                  .slice(0, 3);
                setHeightCm(digitsOnly);
                // heightCm lives outside the form, so its error is
                // cleared by hand rather than by setValue.
                if (errors.height)
                  setErrors((prev) => ({ ...prev, height: undefined }));
              }}
              invalid={!!errors.height}
              placeholder="e.g., 180"
            />
            <FieldError>{errors.height}</FieldError>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default UserModal;
